/**
 * Was die Übersicht anmahnt, in der Reihenfolge, in der es zählt.
 *
 * Bis Oktober 2026 standen bis zu vier Warnkästen übereinander, und keiner
 * war wichtiger als der andere. Jetzt steht oben genau eine Warnung (Auftrag
 * 010, D1), die übrigen als Zeilen in „Zu prüfen“. Reihenfolge:
 * Engpass > Kontostand fehlt > Kontostand alt > Burn-Abweichung. Fehlt der
 * Kontostand, entfällt der Engpass: Er wäre nur ein Echo davon.
 *
 * Die Burn-Abweichung gibt es nur mit Buchungen im Fenster (U13): Ohne
 * Buchung ist der Ist-Burn 0 €, und „0 € Ist gegen 14.000 € Plan“ ist keine
 * Abweichung, sondern eine Lücke in den Daten.
 */

import { formatDatum, formatEuro, formatMonat } from '@elbklar/ui';

import type { Overview } from '@/types';

export type WarnungSchluessel = 'engpass' | 'kontostand_fehlt' | 'kontostand_alt' | 'burn_abweichung';

export interface Warnung {
  schluessel: WarnungSchluessel;
  ton: 'danger' | 'warning';
  titel: string;
  text: string;
  /** Wege zum Beheben, der erste ist der übliche. */
  links: { to: string; label: string }[];
}

/** Ab wie vielen Monaten ein Engpass auf der Übersicht steht. */
export const ENGPASS_MONATE = 6;

/** Ab welcher Abweichung (€ pro Monat) der Ist-Burn auffällt. */
export const BURN_SCHWELLE = 500;

/** Gibt es im Ist-Fenster Buchungen? Ohne sie sind Ist-Werte keine Messung (U13). */
export function hatIst(d: Overview): boolean {
  return d.actual_transaction_count > 0;
}

export function warnungen(d: Overview): Warnung[] {
  const liste: Warnung[] = [];

  // Ohne Kontostand startet die Projektion bei null und „reißt“ den Puffer
  // sofort: Ein Engpass daraus ist keine Aussage, nur die Folge der Lücke.
  if (d.balance_as_of !== null && d.shortfall_months !== null && d.shortfall_months <= ENGPASS_MONATE) {
    const monate = d.shortfall_months === 1 ? '1 Monat' : `${d.shortfall_months} Monaten`;
    liste.push({
      schluessel: 'engpass',
      ton: 'danger',
      titel: d.shortfall_months === 0 ? 'Engpass jetzt' : `Engpass in ${monate}`,
      text:
        `Ab ${formatMonat(d.shortfall_date)} liegt der Kontostand unter dem Mindestpuffer von ${formatEuro(d.min_buffer)}.` +
        (d.min_balance_label ? ` Tiefpunkt ${formatEuro(d.min_balance)} im ${formatMonat(d.min_balance_label)}.` : ''),
      links: [{ to: '/planung', label: 'Planung öffnen' }],
    });
  }

  if (d.balance_as_of === null) {
    liste.push({
      schluessel: 'kontostand_fehlt',
      ton: 'warning',
      titel: 'Kontostand fehlt',
      text: 'Ohne Startsaldo rechnet die Projektion nur Veränderungen. Runway und Engpass sind damit nicht aussagekräftig.',
      links: [
        { to: '/einstellungen?reiter=kontostand', label: 'Kontostand eintragen' },
        { to: '/import', label: 'Kontoauszug importieren' },
      ],
    });
  } else if (Number(d.carry_forward) !== 0) {
    liste.push({
      schluessel: 'kontostand_alt',
      ton: 'warning',
      titel: `Kontostand vom ${formatDatum(d.balance_as_of)}`,
      text: `Seither sind ${formatEuro(d.carry_forward, { cent: true })} eingeplant, die in der Kurve stecken, aber von der Bank nicht bestätigt sind.`,
      links: [{ to: '/einstellungen?reiter=kontostand', label: 'Kontostand nachtragen' }],
    });
  }

  if (hatIst(d) && Number(d.burn_gap) > BURN_SCHWELLE) {
    liste.push({
      schluessel: 'burn_abweichung',
      ton: 'warning',
      titel: `Abfluss ${formatEuro(d.burn_gap)} über Plan`,
      text: 'Es laufen Kosten, die nicht als Fixkosten hinterlegt sind.',
      links: [{ to: '/buchungen?uncategorized=1', label: 'Unkategorisierte Buchungen ansehen' }],
    });
  }

  return liste;
}
